import BaseInput from '@cohort/merchants/components/form/v2/BaseInput';
import DatetimeInput from '@cohort/merchants/components/form/v2/DatetimeInput';
import SelectInput from '@cohort/merchants/components/form/v2/SelectInput';
import type {FormField} from '@cohort/merchants/lib/form/utils';
import {formatDate} from '@cohort/shared/utils/format';
import {ArrowUUpLeft, PencilSimple, TrashSimple} from '@phosphor-icons/react';
import {useState} from 'react';
import type {FieldValues} from 'react-hook-form';
import {useController} from 'react-hook-form';
import {match} from 'ts-pattern';

type EditModeFieldProps<T extends FieldValues> = Omit<JSX.IntrinsicElements['input'], 'name'> &
  FormField<T> & {
    dataType: 'string' | 'number' | 'boolean' | 'date';
  };

// eslint-disable-next-line @typescript-eslint/naming-convention
function EditModeField<T extends FieldValues>({
  dataType,
  control,
  register,
  name,
}: EditModeFieldProps<T>): JSX.Element {
  const {field} = useController({name, control});

  return match(dataType)
    .with('date', () => <DatetimeInput control={control} name={name} register={register} />)
    .with('string', () => (
      <BaseInput {...register(name)} value={field.value} placeholder="-" type="text" />
    ))
    .with('number', () => (
      <BaseInput
        {...register(name, {
          setValueAs: (value: string) => (value === '' ? '' : parseFloat(value)),
        })}
        value={field.value}
        placeholder="-"
        type="number"
      />
    ))
    .with('boolean', () => (
      <SelectInput
        control={control}
        name={name}
        register={register}
        options={[
          {label: 'true', value: true},
          {label: 'false', value: false},
        ]}
      />
    ))
    .exhaustive();
}

type ReadOnlyModeFieldProps = {
  dataType: 'string' | 'number' | 'boolean' | 'date';
  value: unknown;
};
const ReadOnlyModeField: React.FC<ReadOnlyModeFieldProps> = ({dataType, value}) => {
  if (value === undefined || value === '') {
    return <p className="text-slate-500">-</p>;
  }
  return (
    <p className="line-clamp-2 font-medium">
      {dataType === 'date' ? formatDate(value as Date) : String(value)}
    </p>
  );
};

type UserPropertyCellProps<T extends FieldValues> = Omit<
  JSX.IntrinsicElements['input'],
  'name' | 'defaultValue'
> &
  FormField<T> & {
    dataType: 'string' | 'number' | 'boolean' | 'date';
    defaultValue: string | number | boolean | Date;
    withRemoveButton?: boolean;
  };

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function EditableField<T extends FieldValues>({
  dataType,
  control,
  register,
  name,
  defaultValue,
  withRemoveButton = false,
  disabled = false,
}: UserPropertyCellProps<T>): JSX.Element {
  const {field} = useController({name, control});
  const [editMode, setEditMode] = useState(false);
  return (
    <div className="flex flex-row items-center justify-between space-x-3 text-sm">
      {editMode ? (
        <EditModeField control={control} name={name} register={register} dataType={dataType} />
      ) : (
        <ReadOnlyModeField dataType={dataType} value={field.value} />
      )}
      {disabled === false && (
        <div className="flex flex-row items-center space-x-2">
          {editMode && withRemoveButton && (
            <TrashSimple
              className="h-5 w-5 flex-shrink-0 cursor-pointer text-slate-400"
              onClick={() => field.onChange('')}
            />
          )}
          {editMode ? (
            <ArrowUUpLeft
              className="h-5 w-5 flex-shrink-0 cursor-pointer text-slate-400"
              onClick={() => {
                setEditMode(false);
                field.onChange(defaultValue);
              }}
            />
          ) : (
            <PencilSimple
              className="h-5 w-5 flex-shrink-0 cursor-pointer text-slate-400"
              onClick={() => setEditMode(true)}
            />
          )}
        </div>
      )}
    </div>
  );
}
